import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { useSession } from '../../state/useSession';
import { useProjects } from '../../state/projects';
import { isUnfinished, isWebApp } from '@/webapp/webAppRecord';
import type { AppDefinition } from '@/types/app';
import ChatPanel from './ChatPanel';
import PublishPanel from './PublishPanel';
import SiteSettings from './SiteSettings';
import UnsupportedRuntime from './UnsupportedRuntime';
import UnfinishedBuild from './UnfinishedBuild';
import SiteLine, { SiteStatusChip } from './SiteLine';
import ManagePanel from './manage/ManagePanel';
import { Link } from 'react-router';
import { useSiteCheck } from '../../state/siteStatus';
import { accountsIn, addSignInRequest, reachOf, signInSummary } from '../../state/people';
import { Button } from '../../ui';

/**
 * The editor's three places: Chat, where the site is changed; Your data, what the phone calls
 * Manage; and Settings — whether the page is up, who can open it, sign-in, replies and people.
 */
type View = 'chat' | 'data' | 'settings';

const VIEW_LABEL: Record<View, string> = { chat: 'Chat', data: 'Your data', settings: 'Settings' };

/**
 * `?mode=` (or a route's `mode`) as a view. `manage` is the phone's word for Your data, and what
 * older links say. Anything else lands on Chat — the build's old `?mode=preview` and the
 * `/project/:id/preview` route included: there is no preview any more, and Chat is where a
 * finished build is looked at from (its link opens the site itself).
 */
function viewFrom(param: string | null | undefined): View {
  if (param === 'data' || param === 'manage') return 'data';
  if (param === 'settings') return 'settings';
  return 'chat';
}

/**
 * The web editor: the chat fills the page, and the site itself is one link away.
 *
 * The owner, 2026-09-29: "Let's ensure the editing flow is better and easier to navigate. I
 * should not even see the preview — all I need is the link to a new tab and the chat." The
 * preview frame is gone, and with it the split it forced on a laptop and the tab it took on a
 * phone. Nothing here draws the generated page — not in a frame, not from a blob: or data: URL,
 * either of which would run it where it could read the builder's tokens. The top bar holds the
 * site's name, whether it is up, and "Open your site ↗" to the SERVED page, in a new tab.
 *
 * Chat is kept mounted across views, hidden rather than thrown away, so words half typed and
 * files picked there survive a look at Your data or Settings.
 */
export default function ProjectEditor({ mode }: { mode?: View | 'manage' | 'preview' }) {
  const { id = '' } = useParams();
  const { user } = useSession();
  const [params] = useSearchParams();
  // A new entry on every navigation — including the build's own, which lands on the SAME
  // /project/:id when it fills a reserved row, and must not keep showing the unfinished one.
  const { key: visit } = useLocation();
  const project = useProjects((s) => s.currentProject);
  const [view, setView] = useState<View>(viewFrom(mode ?? params.get('mode')));
  /*
   * Your data and Settings are drawn the first time they are opened and then KEPT, hidden, like
   * Chat: a row half typed in Your data survives a look at Chat, and a Take offline on its way
   * in Settings still lands. Not drawn before that, because Your data's Live section asks the
   * server for the site's rows, and an owner who only came to chat has not asked for them.
   */
  const [opened, setOpened] = useState<Record<View, boolean>>({ chat: true, data: view === 'data', settings: view === 'settings' });
  const choose = useCallback((next: View) => {
    setView(next);
    setOpened((o) => (o[next] ? o : { ...o, [next]: true }));
  }, []);
  // A chat edit on its way, and a Your data save on its way. Each publishes itself when it lands,
  // so Publish waits for both — and Your data will not rewrite the source under a chat edit.
  const [chatBusy, setChatBusy] = useState(false);
  const [manageBusy, setManageBusy] = useState(false);
  const editing = chatBusy || manageBusy;

  /*
   * Words Your data asks to have said in Chat — "Add sign-in" — handed to the composer for the
   * owner to read and send, never sent for them. Numbered, so asking for the same words twice is
   * two requests. The phone's `askInChat` (src/app/(main)/project/[id].tsx).
   */
  const [chatPrefill, setChatPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const asked = useRef(0);
  const askInChat = useCallback((text: string) => {
    asked.current += 1;
    setChatPrefill({ text, nonce: asked.current });
    setView('chat');
  }, []);
  const openSettings = useCallback(() => choose('settings'), [choose]);

  /*
   * Whether opening this project has settled, and how. A null project used to render "Loading…"
   * for ever: a /project/:id link opened in another browser, in Instagram's in-app browser or
   * after site data was cleared had no end and no way out (F18, deep-dive 2026-09-30). Read from
   * the store once the load has finished — `loadProject` leaves `currentProject` null when there
   * is no such project, and says why in `error` when it could not look.
   */
  const [opening, setOpening] = useState<{ id: string; missing: boolean; error: string | null } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setOpening(null);
    void Promise.resolve(useProjects.getState().loadProject(id, user?.id ?? ''))
      .catch(() => undefined)
      .then(() => {
        if (!live) return;
        const after = useProjects.getState();
        setOpening({ id, missing: after.currentProject?.id !== id, error: after.error ?? null });
      });
    return () => {
      live = false;
    };
  }, [id, user?.id, visit, attempt]);

  /*
   * Whether the page is up, asked of the server once and shown in two places: in the top bar and
   * at the top of Settings, which is also what changes it. Asked here, above the early returns,
   * because it is a hook; only an app that can have a page has one to ask about.
   */
  const current = project && project.id === id ? (project.definition as AppDefinition) : null;
  const pageable = current && isWebApp(current) && !isUnfinished(current) ? current : null;
  const [site, setSite] = useSiteCheck(pageable?.hostedAppId);

  if (!project || project.id !== id) {
    const settled = opening?.id === id ? opening : null;
    if (settled?.error) {
      return (
        <section className="editor-missing" aria-label="This app could not be opened">
          <p role="alert" className="type-body">{settled.error}</p>
          <div className="build-actions">
            <Button title="Try again" onPress={() => setAttempt((n) => n + 1)} />
          </div>
          <p className="type-body"><Link to="/my-apps">Go to My Apps</Link></p>
        </section>
      );
    }
    if (settled?.missing) {
      /*
       * Said about the app, not about this browser: projects are moving to the server (/me/projects),
       * and then "not in this browser" would be untrue of an app that was deleted. My Apps lists the
       * person's live apps made elsewhere too, each opening its page.
       */
      return (
        <section className="editor-missing" aria-labelledby="editor-missing-title">
          <h1 id="editor-missing-title" className="type-heading">We can’t find this app</h1>
          <p className="type-body">
            It may have been made in another browser, or deleted. Everything you have made is in My Apps.
          </p>
          <p className="type-body"><Link to="/my-apps">Go to My Apps</Link></p>
        </section>
      );
    }
    return <p className="type-body">Loading…</p>;
  }

  const definition = project.definition as AppDefinition;
  if (!isWebApp(definition)) return <UnsupportedRuntime runtime={definition.runtime ?? 'engine'} />;
  // Read by the MARK, never by the source: a gate failure can leave text that is not an app.
  if (isUnfinished(definition)) return <UnfinishedBuild project={project} />;

  const source = definition.source ?? '';
  /*
   * Whether the site has sign-in, in the phone's sentence (state/people.ts). The owner asked how
   * to "enable user management" and found nothing: People lived two screens away under My Apps,
   * and nothing said whether the site had accounts at all.
   */
  const accounts = accountsIn(source);
  const reach = reachOf(site);
  const signIn = signInSummary(
    accounts,
    reach,
    definition.hostedAppId ? 'Everyone who has signed in is listed under People using this site.' : '',
  );

  return (
    <div className="editor">
      <header className="editor-bar">
        {/*
          * The way home. The editor is full-bleed, with no tab bar, and the browser's Back after a
          * build lands on the finished app's plan (F18, deep-dive 2026-09-30).
          */}
        <Link to="/my-apps" className="editor-home">
          <span aria-hidden="true">←</span> My Apps
        </Link>
        <div className="editor-bar-name">
          <h1 className="type-heading editor-name">{project.name}</h1>
          <SiteStatusChip site={site} />
        </div>
        <div className="editor-bar-site">
          <SiteLine webUrl={definition.webUrl} site={site} onOpenSettings={openSettings} />
        </div>
        <div className="editor-toggle" role="tablist" aria-label="Editor view">
          {(['chat', 'data', 'settings'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              className={view === v ? 'is-selected' : ''}
              onClick={() => choose(v)}
            >
              {VIEW_LABEL[v]}
            </button>
          ))}
        </div>
      </header>

      {/* `hidden` on a plain wrapper, so each view keeps what was typed in it while another is shown. */}
      <div className="editor-view editor-chat" hidden={view !== 'chat'}>
        <ChatPanel
          projectId={id}
          appId={definition.id}
          goal={project.description}
          source={source}
          onBusyChange={setChatBusy}
          prefill={chatPrefill}
          onOpenSettings={openSettings}
        />
      </div>

      {opened.data && (
        <div className="editor-view" hidden={view !== 'data'}>
          <ManagePanel
            projectId={id}
            ownerId={user?.id ?? ''}
            definition={definition}
            site={site}
            chatBusy={chatBusy}
            onBusyChange={setManageBusy}
            onAskInChat={askInChat}
          />
        </div>
      )}

      {opened.settings && (
        <div className="editor-view" hidden={view !== 'settings'}>
          <PublishPanel project={project} editing={editing} site={site} onSiteChange={setSite} />
          {/* The name, the address and who pays for AI — the phone's owner tools (F24). */}
          <SiteSettings
            projectId={id}
            name={project.name}
            definition={definition}
            site={site}
            editing={editing}
            onSiteChange={setSite}
          />
          {/*
            * Drawn only while Settings is shown: Your data says all of this itself, with Add
            * sign-in as a button, and said twice in one document it is noise.
            */}
          {view === 'settings' && (
            <>
              <section className="editor-people" aria-label="Sign-in">
                <p className="type-body">
                  <strong>{signIn.headline}</strong> {signIn.detail}
                </p>
                {accounts === 'none' && (
                  <p className="type-caption">{`To add it, ask in Chat: “${addSignInRequest(definition.plan, source)}”`}</p>
                )}
              </section>
              <p className="type-body editor-links">
                <Link to={`/submissions/${id}`}>See replies people sent</Link>
                {/*
                  * Only once the site has a catalogue id: People is read from the server under it,
                  * and an app that was never published has nobody to list.
                  */}
                {definition.hostedAppId && (
                  <>
                    {' · '}
                    <Link to={`/users/${definition.hostedAppId}`} state={{ accounts, reach }}>People using this site</Link>
                  </>
                )}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
